import type {
  OperationsApprovalTimeResponse,
  OperationsComplaintsResponse,
  OperationsOnHoldCanceledResponse,
  OperationsOnTimeRateResponse,
  OperationsProcessingTimeResponse,
  OperationsTotalRequestsResponse,
  OperationsUnderestimatedServicesResponse,
} from "@/types/analytics-operations";
import { OPERATIONS_CATEGORY_COLORS } from "./constants";
import type { MetricCardTrend } from "../shared";
import type {
  OperationsCategorySegment,
  OperationsApprovalTimeChartData,
  OperationsChartDataPoint,
  OperationsComplaintsChartData,
  OperationsExecutionTrendPoint,
  OperationsOnHoldCanceledChartData,
  OperationsRequestsSectionData,
  OperationsSummary,
  OperationsSummaryDelta,
  OperationsTotalRequestsTrendChartData,
  OperationsUnderestimatedServiceRow,
} from "./types";

type OperationsSummaryComparison = {
  totalRequests?: OperationsTotalRequestsResponse;
  processingTime?: OperationsProcessingTimeResponse;
  onTimeRate?: OperationsOnTimeRateResponse;
  complaints?: OperationsComplaintsResponse;
};

function buildDelta(
  percentChange: number | null,
  trend: MetricCardTrend,
): OperationsSummaryDelta | undefined {
  if (percentChange == null) {
    return undefined;
  }

  return {
    text: formatDeltaFromPreviousPeriod(percentChange),
    trend,
  };
}

function toDayLabel(value: string): string {
  const dayFromIso = value.slice(8, 10);
  if (/^\d{2}$/.test(dayFromIso)) {
    return String(Number(dayFromIso));
  }
  return value;
}

function formatMinutesFromSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0m";
  }

  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = Math.round(seconds % 60);

  if (minutes <= 0) {
    return `${remainderSeconds}s`;
  }
  if (remainderSeconds <= 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainderSeconds}s`;
}

function formatPercentChange(value: number): string {
  const rounded = Number(value.toFixed(1));
  if (rounded > 0) {
    return `+${rounded}%`;
  }
  if (rounded < 0) {
    return `${rounded}%`;
  }
  return "0%";
}

function formatDeltaFromPreviousPeriod(percentChange: number): string {
  return `${formatPercentChange(percentChange)} from previous period`;
}

function percentChange(currentValue: number, previousValue: number): number {
  if (previousValue === 0) {
    if (currentValue === 0) {
      return 0;
    }
    return 100;
  }

  return Number(
    (((currentValue - previousValue) / previousValue) * 100).toFixed(1),
  );
}

function formatDelayFromMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) {
    return "0m";
  }

  const wholeMinutes = Math.floor(minutes);
  const seconds = Math.round((minutes - wholeMinutes) * 60);
  if (seconds <= 0) {
    return `+${wholeMinutes}m`;
  }
  return `+${wholeMinutes}m ${seconds}s`;
}

export function mapOperationsSummary(
  totalRequests: OperationsTotalRequestsResponse | undefined,
  processingTime: OperationsProcessingTimeResponse | undefined,
  onTimeRate: OperationsOnTimeRateResponse | undefined,
  complaints: OperationsComplaintsResponse | undefined,
  previous?: OperationsSummaryComparison,
): OperationsSummary {
  const totalRequestsChange = previous?.totalRequests
    ? percentChange(
        totalRequests?.totals.total ?? 0,
        previous.totalRequests.totals.total,
      )
    : null;
  const processingTimeChange = previous?.processingTime
    ? percentChange(
        processingTime?.totals.avgSeconds ?? 0,
        previous.processingTime.totals.avgSeconds,
      )
    : null;
  const onTimeChange =
    onTimeRate?.totals.onTimePercent != null &&
    previous?.onTimeRate?.totals.onTimePercent != null
      ? percentChange(
          onTimeRate.totals.onTimePercent,
          previous.onTimeRate.totals.onTimePercent,
        )
      : null;
  const complaintsChange = previous?.complaints
    ? percentChange(
        complaints?.totals.complaintCount ?? 0,
        previous.complaints.totals.complaintCount,
      )
    : null;

  const processingDeltaTrend: MetricCardTrend =
    processingTimeChange != null && processingTimeChange <= 0
      ? "positive"
      : "negative";
  const complaintsDeltaTrend: MetricCardTrend = "negative";

  return {
    totalRequests: totalRequests?.totals.total ?? 0,
    avgProcessingTime:
      processingTime?.totals.humanReadable ??
      formatMinutesFromSeconds(processingTime?.totals.avgSeconds ?? 0),
    onTimePercent:
      onTimeRate?.totals.onTimePercent != null
        ? Math.round(onTimeRate.totals.onTimePercent)
        : null,
    complaintCount: complaints?.totals.complaintCount ?? 0,
    deltaTotalRequests: buildDelta(
      totalRequestsChange,
      totalRequestsChange != null && totalRequestsChange >= 0
        ? "positive"
        : "negative",
    ),
    deltaAvgProcessingTime: buildDelta(
      processingTimeChange,
      processingDeltaTrend,
    ),
    deltaOnTimePercent: buildDelta(
      onTimeChange,
      onTimeChange != null && onTimeChange >= 0 ? "positive" : "negative",
    ),
    deltaComplaints: buildDelta(complaintsChange, complaintsDeltaTrend),
  };
}

export function mapOperationsExecutionTrend(
  onTimeRate: OperationsOnTimeRateResponse | undefined,
  processingTime: OperationsProcessingTimeResponse | undefined,
): OperationsExecutionTrendPoint[] {
  if (!onTimeRate && !processingTime) {
    return [];
  }

  const processingByDay = new Map(
    (processingTime?.series ?? []).map((item) => [item.day, item]),
  );

  return (onTimeRate?.series ?? [])
    .filter((item) => item.onTimePercent != null)
    .map((item) => {
      const processing = processingByDay.get(item.day);
      return {
        day: toDayLabel(item.day),
        onTimePercent: Number(item.onTimePercent!.toFixed(1)),
        avgTime: Number(((processing?.avgSeconds ?? 0) / 60).toFixed(1)),
      };
    });
}

export function mapOperationsRequestsSection(
  totalRequests: OperationsTotalRequestsResponse | undefined,
  underestimated: OperationsUnderestimatedServicesResponse | undefined,
): OperationsRequestsSectionData {
  const totals = totalRequests?.totals;
  const categorySegments: OperationsCategorySegment[] = [
    {
      key: "roomServices",
      name: "Room services",
      value: totals?.roomServices ?? 0,
      fill: OPERATIONS_CATEGORY_COLORS.roomServices,
    },
    {
      key: "guestServices",
      name: "Guest services",
      value: totals?.guestServices ?? 0,
      fill: OPERATIONS_CATEGORY_COLORS.guestServices,
    },
    {
      key: "externalServices",
      name: "External services",
      value: totals?.externalServices ?? 0,
      fill: OPERATIONS_CATEGORY_COLORS.externalServices,
    },
  ];

  const rows: OperationsUnderestimatedServiceRow[] = (
    underestimated?.services ?? []
  ).map((row, index) => ({
    id: row.serviceId,
    rate: index + 1,
    name: row.serviceName,
    latePercent: Math.round(row.latePercent ?? 0),
    avgDelayTime: formatDelayFromMinutes(row.avgDelayMinutes),
    lateRequests: row.lateRequests,
    totalRequests: row.totalRequests,
  }));

  return {
    categorySegments,
    categoryTotal: totals?.total ?? 0,
    underestimatedSummary: {
      totalRequests: underestimated?.totals.totalRequests ?? 0,
      onTimePercent: Math.round(
        100 - (underestimated?.totals.latePercent ?? 0),
      ),
      avgDelay: formatDelayFromMinutes(underestimated?.totals.avgDelayMinutes),
    },
    underestimatedServices: rows,
  };
}

export function mapOnHoldCanceledChart(
  response: OperationsOnHoldCanceledResponse | undefined,
): OperationsOnHoldCanceledChartData {
  return {
    data: (response?.series ?? []).map((item) => ({
      day: toDayLabel(item.day),
      canceledRequests: item.canceled,
      onHoldRequests: item.onHold,
    })),
  };
}

export function mapApprovalTimeChart(
  response: OperationsApprovalTimeResponse | undefined,
): OperationsApprovalTimeChartData {
  return {
    data: (response?.series ?? []).map((item) => ({
      day: toDayLabel(item.day),
      frontdeskApprovalTime: Number((item.avgSeconds / 60).toFixed(2)),
    })),
  };
}

export function mapComplaintsChart(
  response: OperationsComplaintsResponse | undefined,
): OperationsComplaintsChartData {
  return {
    data: (response?.series ?? []).map((item) => ({
      day: toDayLabel(item.day),
      complaintCount: item.complaintCount,
    })),
  };
}

export function mapTotalRequestsTrendChart(
  response: OperationsTotalRequestsResponse | undefined,
): OperationsTotalRequestsTrendChartData {
  return {
    data: (response?.series ?? []).map((item) => ({
      day: toDayLabel(item.day),
      guestServices: item.guestServices,
      roomServices: item.roomServices,
      externalServices: item.externalServices,
    })),
  };
}

export function computeOperationsChartYMax(
  data: OperationsChartDataPoint[],
  seriesKeys: string[],
  fallback = 50,
): number {
  let max = 0;
  for (const point of data) {
    for (const key of seriesKeys) {
      const value = point[key];
      if (typeof value === "number") {
        max = Math.max(max, value);
      }
    }
  }
  if (max <= 0) {
    return fallback;
  }
  return Math.max(10, Math.ceil(max * 1.15));
}
